import React, { useCallback, useState } from 'react';
import { Button, Card, Col, Collapse, Form, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';

import { AccountTopicPreference, ALERT_FREQUENCY, ProgramModel } from '@/lib/models';
import {
	accountService,
	AlertPreferencesRequestBody,
	AlertPreferencesResponseBody,
	programSettingService,
} from '@/lib/services';
import { useAccount, useHandleError, useNavigationConfig } from '@/hooks';
import { AsyncPage, BlogFollowModel } from '@/components';

export const EmailCommunications = () => {
	const { account } = useAccount();
	const handleError = useHandleError();
	const { navigationConfig } = useNavigationConfig();
	const [isSaving, setIsSaving] = useState(false);
	const [showFollowModal, setShowFollowModal] = useState(false);
	const [contentAlertPreferences, setContentAlertPreferences] = useState<AlertPreferencesResponseBody>({
		announcementsPreference: false,
		authorsPreference: false,
		eventsPreference: false,
		authors: [],
		frequency: null,
		newTopics: [],
		performanceStudyPreference: false,
		topicPreference: false,
		topicPreferences: [],
	});
	const [newsletters, setNewsletters] = useState<ProgramModel[]>([]);

	const fetchData = useCallback(async () => {
		if (!account) {
			throw new Error('account is undefined.');
		}

		const [contentAlertPreferencesResponse, newsletterResponse] = await Promise.all([
			accountService.getAlertPreferences().fetch(),
			programSettingService.getPreferences(account.accountId).fetch(),
		]);

		setContentAlertPreferences(contentAlertPreferencesResponse);
		setNewsletters(newsletterResponse.programs);
	}, [account]);

	const handlePreferenceChange = useCallback(
		async (data: Partial<AlertPreferencesRequestBody>) => {
			setIsSaving(true);

			try {
				const response = await accountService
					.updateAlertPreferences({
						announcementsPreference: contentAlertPreferences.announcementsPreference,
						authorsPreference: contentAlertPreferences.authorsPreference,
						eventsPreference: contentAlertPreferences.eventsPreference,
						followedAuthors: contentAlertPreferences.authors.filter(a => a.userIsFollowing).map((i) => i.advisorId),
						frequency: contentAlertPreferences.frequency,
						performanceStudyPreference: contentAlertPreferences.performanceStudyPreference,
						topics: [
							...contentAlertPreferences.topicPreferences
								.filter((t) => t.enabled)
								.map((t) => t.topicName),
							...contentAlertPreferences.newTopics.filter((t) => t.enabled).map((t) => t.topicName),
						],
						topicPreference: contentAlertPreferences.topicPreference,
						...data,
					})
					.fetch();

				setContentAlertPreferences(response);
			} catch (error) {
				handleError(error);
			} finally {
				setIsSaving(false);
			}
		},
		[
			contentAlertPreferences.announcementsPreference,
			contentAlertPreferences.authorsPreference,
			contentAlertPreferences.eventsPreference,
			contentAlertPreferences.authors,
			contentAlertPreferences.frequency,
			contentAlertPreferences.newTopics,
			contentAlertPreferences.performanceStudyPreference,
			contentAlertPreferences.topicPreference,
			contentAlertPreferences.topicPreferences,
			handleError,
		]
	);

	const handleNewsletterCheckboxChange = useCallback(
		async ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
			setIsSaving(true);

			try {
				if (!account) {
					throw new Error('account is undefined.');
				}

				const response = await programSettingService
					.setPreferences(account.accountId, {
						accountCommPreferences: newsletters
							.map((p) =>
								p.programId === currentTarget.value ? { ...p, allowEmail: currentTarget.checked } : p
							)
							.filter(({ allowUserPreferences }) => allowUserPreferences)
							.map(({ programId, allowEmail, allowAdvisoryDisplay }) => ({
								allowAdvisoryDisplay,
								allowEmails: allowEmail,
								programId,
							})),
					})
					.fetch();

				setNewsletters(response.programs);
			} catch (error) {
				handleError(error);
			} finally {
				setIsSaving(false);
			}
		},
		[account, newsletters, handleError]
	);

	return (
		<AsyncPage fetchData={fetchData}>
			<Helmet>
				<title>Hackett Connect - Account - Email Communications</title>
			</Helmet>

			<BlogFollowModel
				authors={contentAlertPreferences.authors}
				show={showFollowModal}
				onHide={() => {
					setShowFollowModal(false);
				}}
				onChange={(authors) => {
					setContentAlertPreferences((pv) => ({ ...pv, authors }));
				}}
			/>

			<Row>
				<Col>
					<Form>
						<fieldset disabled={isSaving}>
							{navigationConfig?.allowAlerts && (
								<Card className="mb-6">
									<Card.Header>
										<Card.Title className="large">Email Alerts</Card.Title>
									</Card.Header>
									<Card.Body>
										<TopicAlert
											className="mb-6"
											topicsPreference={contentAlertPreferences.topicPreference}
											newTopics={contentAlertPreferences.newTopics}
											topics={contentAlertPreferences.topicPreferences}
											onTopicsPreferenceChange={(pref) => {
												handlePreferenceChange({
													topicPreference: pref,
												});
											}}
											onTopicsChange={(newTopics) => {
												handlePreferenceChange({
													topics: newTopics.filter((i) => i.enabled).map((i) => i.topicName),
												});
											}}
										/>
										<div className="mb-6">
											<Form.Check
												type="checkbox"
												className="mb-1"
												name="author-activity"
												id="author-activity"
												label="Activity from Authors You Follow"
												value="TRUE"
												checked={contentAlertPreferences.authorsPreference}
												onChange={({ currentTarget }) => {
													handlePreferenceChange({
														authorsPreference: currentTarget.checked,
													});
												}}
											/>
											<div className="pl-5">
												<p className="mb-1 small text-gray-800">
													An author you follow posts a new research document or blog post
												</p>
												<Button
													variant="link"
													className="font-weight-bold"
													onClick={() => {
														setShowFollowModal(true);
													}}
												>
													Update authors I follow ({contentAlertPreferences.authors.filter(a => a.userIsFollowing).length}/
														{contentAlertPreferences.authors.length})
												</Button>
											</div>
										</div>
										<div className="mb-6">
											<Form.Check
												type="checkbox"
												className="mb-1"
												name="performanceStudyPreference"
												id="performance-study-preference"
												label="New Performance Studies"
												value="TRUE"
												checked={contentAlertPreferences.performanceStudyPreference}
												onChange={({ currentTarget }) => {
													handlePreferenceChange({
														performanceStudyPreference: currentTarget.checked,
													});
												}}
											/>
											<div className="pl-5">
												<p className="mb-0 small text-gray-800">
													Learn about new performance studies added in your programs
												</p>
											</div>
										</div>
										<div className="mb-6">
											<Form.Check
												type="checkbox"
												className="mb-1"
												name="eventsPreference"
												id="events-preference"
												label="New Events"
												value="TRUE"
												checked={contentAlertPreferences.eventsPreference}
												onChange={({ currentTarget }) => {
													handlePreferenceChange({
														eventsPreference: currentTarget.checked,
													});
												}}
											/>
											<div className="pl-5">
												<p className="mb-0 small text-gray-800">
													Learn about new events added in your programs
												</p>
											</div>
										</div>
										<div className="mb-6">
											<Form.Check
												type="checkbox"
												className="mb-1"
												name="announcementsPreference"
												id="announcements-preference"
												label={
													<>
												    Updates from Hackett Connect<sup>&reg;</sup>
												    </>
												}
												value="TRUE"
												checked={contentAlertPreferences.announcementsPreference}
												onChange={({ currentTarget }) => {
													handlePreferenceChange({
														announcementsPreference: currentTarget.checked,
													});
												}}
											/>
											<div className="pl-5">
												<p className="mb-0 small text-gray-800">
													Occasional product and company announcements
												</p>
											</div>
										</div>
										<hr className="mb-6" />
										<h3 className="mb-6">Alert Frequency</h3>
										<p className="mb-6">
											Choose how often you would like to receive email notifications.
										</p>
										<Form.Check
											type="radio"
											className="mb-4"
											name="alert-frequency"
											id="alert-frequency__never"
											label="Never"
											value={undefined}
											checked={!contentAlertPreferences.frequency}
											onChange={() => {
												handlePreferenceChange({
													frequency: null,
												});
											}}
										/>
										<Form.Check
											type="radio"
											className="mb-4"
											name="alert-frequency"
											id="alert-frequency__daily"
											label="Daily"
											value={ALERT_FREQUENCY.DAILY}
											checked={contentAlertPreferences.frequency === ALERT_FREQUENCY.DAILY}
											onChange={() => {
												handlePreferenceChange({
													frequency: ALERT_FREQUENCY.DAILY,
												});
											}}
										/>
										<Form.Check
											type="radio"
											className="mb-4"
											name="alert-frequency"
											id="alert-frequency__weekly"
											label="Weekly"
											value={ALERT_FREQUENCY.WEEKLY}
											checked={contentAlertPreferences.frequency === ALERT_FREQUENCY.WEEKLY}
											onChange={() => {
												handlePreferenceChange({
													frequency: ALERT_FREQUENCY.WEEKLY,
												});
											}}
										/>

										<Form.Check
											type="radio"
											className="mb-0"
											name="alert-frequency"
											id="alert-frequency__monthly"
											label="Monthly"
											value={ALERT_FREQUENCY.MONTHLY}
											checked={contentAlertPreferences.frequency === ALERT_FREQUENCY.MONTHLY}
											onChange={() => {
												handlePreferenceChange({
													frequency: ALERT_FREQUENCY.MONTHLY,
												});
											}}
										/>
									</Card.Body>
								</Card>
							)}
							<Card>
								<Card.Header>
									<Card.Title className="large">Program Newsletters</Card.Title>
								</Card.Header>
								<Card.Body>
									<p className="mb-6">
										Newsletters include general information, invitations to participate in
										performance assessments, webcasts, and other events from your programs.
									</p>
									{newsletters.filter(({ allowUserPreferences }) => allowUserPreferences).length <=
										0 && <p className="mb-0">No programs</p>}
									{newsletters
										.filter(({ allowUserPreferences }) => allowUserPreferences)
										.map(({ programId, name, allowEmail }, programIndex) => {
											const isLast =
												newsletters.filter(({ allowUserPreferences }) => allowUserPreferences)
													.length -
													1 ===
												programIndex;

											return (
												<Form.Check
													type="checkbox"
													key={programId}
													className={classNames({ 'mb-4': !isLast })}
													name="program-newsletters"
													id={`program-newslestters-${programId}`}
													label={name}
													value={programId}
													checked={allowEmail}
													onChange={handleNewsletterCheckboxChange}
												/>
											);
										})}
								</Card.Body>
							</Card>
						</fieldset>
					</Form>
				</Col>
			</Row>
		</AsyncPage>
	);
};

interface TopicAlertProps {
	topicsPreference: boolean;
	onTopicsPreferenceChange(topicsPreference: boolean): void;
	topics: AccountTopicPreference[];
	onTopicsChange(topics: AccountTopicPreference[]): void;
	newTopics: AccountTopicPreference[];
	className?: string;
}

const TopicAlert = ({
	topicsPreference,
	onTopicsPreferenceChange,
	topics,
	onTopicsChange,
	newTopics,
	className,
}: TopicAlertProps) => {
	const [show, setShow] = useState(false);

	const handleTopicsPreferenceCheckboxChange = useCallback(
		({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
			onTopicsPreferenceChange(currentTarget.checked);
		},
		[onTopicsPreferenceChange]
	);

	const handleTopicCheckboxChange = useCallback(
		({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
			onTopicsChange([
				...topics.map((t) =>
					t.topicId === currentTarget.value ? { ...t, enabled: currentTarget.checked } : t
				),
				...newTopics.map((t) =>
					t.topicId === currentTarget.value ? { ...t, enabled: currentTarget.checked } : t
				),
			]);
		},
		[newTopics, onTopicsChange, topics]
	);

	const selectedTopicsLength = [...topics.filter((t) => t.enabled), ...newTopics.filter((t) => t.enabled)].length;
	const topicsLength = [...topics, ...newTopics].length;

	return (
		<div className={className}>
			<Form.Check
				type="checkbox"
				className="mb-1"
				name="topics-preference"
				id="topics-preference"
				label="Updates in Your Topics"
				value="TOPICS_PREFERENCE"
				checked={topicsPreference}
				onChange={handleTopicsPreferenceCheckboxChange}
			/>
			<div className="pl-5">
				<p className="mb-1 small text-gray-800">New research documents or blog posts are added</p>
				<Button
					variant="link"
					className="font-weight-bold"
					onClick={() => {
						setShow((pv) => !pv);
					}}
				>
					Update individual topics ({selectedTopicsLength}/{topicsLength})
				</Button>
				<Collapse in={show}>
					<div>
						<div className="pt-4">
							<div className="d-flex bg-gray-200">
								<div className="w-50 p-5 border-right">
									<ul className="p-0 m-0 list-unstyled">
										{topics.map((topic, topicIndex) => {
											const isLast = topics.length - 1 === topicIndex;

											return (
												<li
													key={topic.topicId}
													className={classNames({ 'mb-0': isLast, 'mb-4': !isLast })}
												>
													<Form.Check
														type="checkbox"
														name="followed-topics"
														id={topic.topicId}
														label={topic.topicName}
														value={topic.topicId}
														checked={topic.enabled}
														onChange={handleTopicCheckboxChange}
													/>
												</li>
											);
										})}
									</ul>
								</div>
								<div className="w-50 p-5 border-right">
									<h4 className="mb-4 text-gray-700 text-uppercase">Newly Added Topics</h4>
									<ul className="p-0 m-0 list-unstyled">
										{newTopics.map((topic, topicIndex) => {
											const isLast = topics.length - 1 === topicIndex;

											return (
												<li
													key={topic.topicId}
													className={classNames({ 'mb-0': isLast, 'mb-4': !isLast })}
												>
													<Form.Check
														type="checkbox"
														name="followed-topics"
														id={topic.topicId}
														label={topic.topicName}
														value={topic.topicId}
														checked={topic.enabled}
														onChange={handleTopicCheckboxChange}
													/>
												</li>
											);
										})}
									</ul>
								</div>
							</div>
						</div>
					</div>
				</Collapse>
			</div>
		</div>
	);
};
